/**
 * LifeMap-Weighted Prioritization Engine client.
 *
 * "yanamas should maximize positive life momentum, not productivity."
 *
 * Every call is non-blocking: the engine API returns 503 when
 * `PRIORITIZATION_ENGINE_ENABLED` is off, and these helpers return null
 * (or [] / false) so the surfaces that use them simply render nothing
 * extra. The Today release card only shows when `release_live` is true.
 *
 * Plan: .cursor/plans/lifemap_prioritization_engine_375edc73.plan.md
 */

export type ReleaseAction =
  | 'release'
  | 'shrink'
  | 'keep_committed'
  | 'hold'
  | 'park';

export type ReleaseStyle = 'full' | 'starter_step' | 'relief';

export interface NotReleased {
  item_id: string;
  reason: string;
  /** Item title (named alternatives — owner ask 2026-08-24). */
  title?: string | null;
  /** task|habit|skill|goal|… — decides which detail route this row opens
   * (fix 2026-08-29: everything used to link to /tasks/<id>). */
  item_type?: string | null;
}

export interface ReleaseDecision {
  /** Outcome already recorded today (done|skipped|rescheduled) — when set,
   * the card must not re-surface (the user already answered). */
  resolved_outcome?: string | null;
  item_id: string | null;
  action: ReleaseAction;
  release_style: ReleaseStyle;
  starter_step: string | null;
  copy: string;
  /** The released item's title, so the card can typographically emphasize
   * the thing the user has to RECOGNIZE inside the caring prose. */
  item_title?: string | null;
  /** The released item's type (task|habit|skill|goal|…), so the card opens
   * the RIGHT detail route (fix 2026-08-29: a released habit used to link
   * to /tasks/<habit_id>, which is not a real URL). */
  item_type?: string | null;
  requires_confirmation: boolean;
  scores: Record<string, number>;
  /** Why this item was released (explainability contract). */
  reasons: string[];
  /** Why each strong contender was held back today. */
  not_released: NotReleased[];
}

export interface TodayReleaseResponse {
  success: boolean;
  release_live: boolean;
  decision: ReleaseDecision;
}

export interface LifeAreaBudget {
  life_area: string;
  importance_points: number;
  max_active: number;
}

export type ReleaseOutcome = 'done' | 'skipped' | 'rescheduled';

/** Fetch today's release decision. Returns null when the engine is off. */
export async function getTodayRelease(): Promise<TodayReleaseResponse | null> {
  try {
    const resp = await fetch('/api/prioritization/v1/today-release', {
      credentials: 'include',
    });
    if (!resp.ok) {
      if (resp.status !== 503) {
        console.warn('[prioritization] today-release returned', resp.status);
      }
      return null;
    }
    return (await resp.json()) as TodayReleaseResponse;
  } catch (e) {
    console.warn('[prioritization] today-release failed (non-blocking):', e);
    return null;
  }
}

/** Record done/skipped/rescheduled feedback for a release decision row. */
export async function recordReleaseOutcome(
  decisionId: number,
  outcome: ReleaseOutcome,
): Promise<boolean> {
  try {
    const resp = await fetch(
      `/api/prioritization/v1/release/${decisionId}/outcome`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome }),
      },
    );
    return resp.ok;
  } catch (e) {
    console.warn('[prioritization] outcome failed (non-blocking):', e);
    return false;
  }
}

/** User-initiated pull: clear a set-aside (skipped/rescheduled) answer so
 *  today's move re-surfaces. Never reverses a recorded 'done'. */
export async function reopenReleaseOutcome(decisionId: number): Promise<boolean> {
  try {
    const resp = await fetch(
      `/api/prioritization/v1/release/${decisionId}/reopen`,
      { method: 'POST', credentials: 'include' },
    );
    return resp.ok;
  } catch (e) {
    console.warn('[prioritization] reopen failed (non-blocking):', e);
    return false;
  }
}

/** List the user's life-area budgets. Returns [] when the engine is off. */
export async function getLifeAreaBudgets(): Promise<LifeAreaBudget[]> {
  try {
    const resp = await fetch('/api/prioritization/v1/budgets', {
      credentials: 'include',
    });
    if (!resp.ok) return [];
    const body = (await resp.json()) as { budgets?: LifeAreaBudget[] };
    return body.budgets ?? [];
  } catch {
    return [];
  }
}

/** Create or update a life-area budget. */
export async function upsertLifeAreaBudget(
  budget: LifeAreaBudget,
): Promise<boolean> {
  try {
    const resp = await fetch('/api/prioritization/v1/budgets', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(budget),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Fill-the-gaps: missing / low-confidence Tier-1 decision signals
// ---------------------------------------------------------------------------

export interface DecisionGapQuestion {
  item_id: string;
  item_type: string;
  item_title: string;
  field: string;
  question: string;
  kind: 'text' | 'level' | 'choice';
  options: string[];
}

export interface DecisionGapItem {
  item_id: string;
  item_type: string;
  item_title: string;
  questions: DecisionGapQuestion[];
}

/** Tier-1 signals AutoMom could gently ask about. [] when engine is off. */
export async function getDecisionGaps(): Promise<DecisionGapItem[]> {
  try {
    const resp = await fetch('/api/prioritization/v1/decision-gaps', {
      credentials: 'include',
    });
    if (!resp.ok) return [];
    const body = (await resp.json()) as { gaps?: DecisionGapItem[] };
    return body.gaps ?? [];
  } catch {
    return [];
  }
}

/** Persist a user's answer (flips provenance to source=user). */
export async function answerDecisionGap(args: {
  item_id: string;
  item_type: string;
  field: string;
  value: string | number | boolean;
}): Promise<boolean> {
  try {
    const resp = await fetch('/api/prioritization/v1/decision-gaps/answer', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

export const LIFE_AREAS: string[] = [
  'health',
  'career',
  'relationships',
  'money',
  'learning',
  'personal_growth',
  'productivity',
  'other',
];
